import {useCallback, useMemo} from 'react'
import {useInfiniteQuery} from '@tanstack/react-query'
import useOcapiSite from '../../hooks/useOcapiSite'
import {useBazaarvoiceAPI} from '../context'
import {BV_REVIEWS_LIMIT} from '../../constants'
import useMultiSite from '../../hooks/use-multi-site'
import {localeToBVLocale} from '../utils'

/**
 * Hook to fetch product reviews for a given product id and query with pagination
 * @param {string} productId
 * @param {boolean} [active] Whether to fetch reviews or not
 * @param {number[]} [rating] Filters to apply to the query
 * @param {number} [pageSize] Page size to apply to the query
 * @param {string} [searchTerm] Search term to apply to the query
 * @param {string} [sort] Sort to apply to the query
 * @returns {{
 *   loadNextPage: (function(): void),
 *   reviews: object[],
 *   totalCount: number|null,
 *   error: Error|null,
 *   loading: boolean
 * }}
 */
const useProductReviews = (
    productId,
    {enabled = true, rating, pageSize = BV_REVIEWS_LIMIT, searchTerm, sort, language} = {}
) => {
    const {locale} = useMultiSite()
    const {bvEnabled} = useOcapiSite()
    const {getProductReviews} = useBazaarvoiceAPI()
    const isEnabled = Boolean(productId && bvEnabled && enabled)

    const query = useMemo(
        () => ({
            Locale: localeToBVLocale(locale),
            Limit: pageSize,
            rating,
            Search: searchTerm,
            Sort: sort,
            contentLocale: language ? locale.id.split('-')[0] : ''
        }),
        [locale, pageSize, rating, searchTerm, sort, language]
    )

    const {data, error, isFetchingNextPage, isInitialLoading, fetchNextPage, hasNextPage} =
        useInfiniteQuery({
            queryKey: ['product-reviews', productId, query],
            queryFn: ({pageParam = 0, signal}) =>
                getProductReviews(productId, {...query, Offset: pageParam, signal}),
            enabled: isEnabled,
            getNextPageParam: (lastPage, allPages) => {
                const currentOffset = (allPages.length - 1) * pageSize
                const nextOffset = currentOffset + pageSize
                return nextOffset < lastPage.totalCount ? nextOffset : undefined
            }
        })

    const loading = isFetchingNextPage || isInitialLoading

    const reviews = useMemo(() => {
        return data?.pages.flatMap((page) => page.reviews) ?? []
    }, [data])

    const totalCount = useMemo(() => {
        return data?.pages[0]?.totalCount ?? null
    }, [data])

    const loadNextPage = useCallback(() => {
        if (hasNextPage && !loading) {
            fetchNextPage()
        }
    }, [fetchNextPage, hasNextPage, loading])

    return {
        error,
        loading,
        loadNextPage,
        reviews,
        totalCount
    }
}

export default useProductReviews
